// tabs/weapon-tables.ts — client port of the Weapons tab's "Equipped Weapons" table
//   options/game/character_sheet_display/weapons_equipped/_weapons_equipped_inc.php (equipped block)
//   + options/game/character_sheet_display/weapons_equipped/weapon_equipped_tr_inc.php (the row)
//
// Equipped weapons are full-nested in the blob (inventory entries with weapon layers), so this renders
// straight from casterCache. Natural weapons (leaned bare-UUIDs needing query_full_item_arr) stay PHP.
// Attack buttons delegate to the existing globals (useAbility / useAbilityVariable1).

import type { CachedCaster } from '../types';
import { uuidOf, catalogNameById } from './ability-row';
import { abilityEffectButton } from './ability-effect-button';
import { decodeHtmlEntitiesForPlainText } from '../../shared/decode-html-entities';
import { ftDispatchWeaponClick } from './action-bar';

type UnknownRecord = Record<string, unknown>;
function recordOf(value: unknown): UnknownRecord | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as UnknownRecord
        : null;
}

type WeaponTablesWindow = Window & {
    openItemInfo?: (itemId: string) => unknown;
    useAbility?: (abilityId: string) => unknown;
    useAbilityVariable1?: (abilityId: string, variable1: string) => unknown;
};

function weaponAttrs(action: string, attrs: Record<string, string> = {}): string {
    const pairs = [`data-ft-weapon-action="${escapeHtml(action)}"`];
    for (const [key, value] of Object.entries(attrs)) {
        pairs.push(`data-ft-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}="${escapeHtml(value)}"`);
    }
    return pairs.join(' ');
}

function weaponInfoAttrs(itemId: string): string {
    return weaponAttrs('open-info', { itemId });
}

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-weapon-action]')
        : null;
    if (!el || !document.contains(el)) return;
    const action = el.dataset.ftWeaponAction ?? '';
    const w = window as WeaponTablesWindow;
    if (action === 'open-info') {
        const itemId = el.dataset.ftItemId ?? '';
        if (itemId !== '') { event.preventDefault(); w.openItemInfo?.(itemId); }
    } else if (action === 'use-ability') {
        const abilityId = el.dataset.ftAbilityId ?? '';
        const casterId = el.dataset.ftCasterId ?? '';
        if (abilityId !== '') {
            event.preventDefault();
            if (!ftDispatchWeaponClick(casterId, abilityId, 'hit')) w.useAbility?.(abilityId);
        }
    } else if (action === 'use-variable1') {
        const abilityId = el.dataset.ftAbilityId ?? '';
        const variable1 = el.dataset.ftVariable1 ?? '';
        const casterId = el.dataset.ftCasterId ?? '';
        if (abilityId !== '') {
            event.preventDefault();
            if (variable1 === 'throw_weapon' && ftDispatchWeaponClick(casterId, abilityId, 'throw')) return;
            const run = () => { w.useAbilityVariable1?.(abilityId, variable1); };
            if (variable1 !== 'damage_only' || !ftDispatchWeaponClick(casterId, abilityId, 'damage', false, run)) run();
        }
    }
});

const SLOT_MAIN = 'CEF334BF-DE96-1BEA-F72A-A4C98FBF36AD';
const SLOT_OFF = '4FF85144-67EC-9D87-AE28-15137A1A2481';
const SLOT_BOTH = 'E6D02ACB-D3E9-DD8B-762A-31FE8C8D898D';
const MELEE_WEAPON_TYPE_ID = '1364260D-E37C-4257-AD16-5E745107811A';
const ICON_ATTACK_MELEE = '2FE8DFB7-FE61-4862-AF71-3523262350A0';
const ICON_ATTACK_RANGED = '3F85CA97-F9B2-A83A-FDE8-E523FD99346C';

function num(v: unknown): number {
    const n = Number(Array.isArray(v) ? v[0] : v);
    return Number.isFinite(n) ? n : 0;
}
function equippedSlotOf(entry: unknown): string {
    return String(ftLayerValue(entry, 'equipped') ?? ftInventoryField(entry, 'equipped') ?? '');
}

/** Port of weapon_equipped_tr_inc.php — one equipped-weapon row. `naturalWeapon` left-aligns the
 *  first column and uses the natural_weapon (claw) icon as the tile face fallback (natural weapons
 *  rarely carry art). */
function weaponRow(caster: CachedCaster, entry: unknown, canEdit: boolean, casterId: string, naturalWeapon = false): string {
    const invId = String(ftInventoryField(entry, 'inventory_id') ?? '');
    // inventory_equipped entries (item-creature model) are full item_arrs with no inventory_* fields →
    // fall back to the nested items.item_id / items.item_name (mirrors the PHP natural-weapon normalize).
    const invItemId = String(ftInventoryField(entry, 'inventory_item_id') ?? ftItemsCol(entry, 'item_id') ?? '');
    // inventory_name / item_name are persisted HTML-ENCODED ("Adon&#039;s Blade"); decode once here so
    // the escapeHtml calls below (name span, img alt, throw tooltip) are the only encoder — otherwise
    // the entity's & is re-encoded and a literal "&#039;" prints. See ability-row.ts.
    const invName = decodeHtmlEntitiesForPlainText(String(ftInventoryField(entry, 'inventory_name') ?? ftItemsCol(entry, 'item_name') ?? ''));
    const openId = invId !== '' ? invId : invItemId;
    const ro = !canEdit;

    // throwing-distance multiplier (read-time scale).
    const throwPct = num(ftLayerValue(caster, 'throwing_distance_changed_by_percentage'));
    const throwMult = (throwPct !== 0 && ftLayerHas(entry, 'item_throwable')) ? 1 + throwPct / 100 : 1;

    // HIT cell — the weapon's PICTURE is the attack-button face, ≈the attack-ability button size, with
    // TWO clean CORNER OVERLAYS mirroring the equipment-slot grid (plain iconMap, no badge/border):
    //   • ROLL TYPE (melee/ranged) top-LEFT,  • EQUIPMENT SLOT top-RIGHT.
    // Overlays only when there's real art (else the fallback attack icon already conveys the roll type
    // and an overlay would just double it — same rule as the equipment grid's slot badge). The image
    // IS the button (no btn chrome). Click fires the attack.
    const attackIcon = String(ftLayerValue(entry, 'weapon_type') ?? '') === MELEE_WEAPON_TYPE_ID ? ICON_ATTACK_MELEE : ICON_ATTACK_RANGED;
    const rollTypeIcon = String(ftLayerValue(entry, 'roll_type') ?? '') || attackIcon;
    // Top-right badge = the hand the weapon is ACTUALLY equipped in (inventory `equipped` — same source
    // as the action bar's renderInventorySlot badge), NOT the item's definitional equipment_slot layer,
    // which always says main-hand for a spear even when it's carried in the off hand. Natural weapons
    // have no `equipped` field → fall back to the layer.
    const slotId = equippedSlotOf(entry) || String(ftLayerValue(entry, 'equipment_slot') ?? '');
    const picObj = typeof window.ftPrimaryPictureOf === 'function' ? recordOf(window.ftPrimaryPictureOf(entry)) : null;
    const picUrlRaw = picObj ? (Array.isArray(picObj.picture_url) ? picObj.picture_url[0] : picObj.picture_url) : '';
    const picUrl = (typeof picUrlRaw === 'string') ? picUrlRaw : '';
    const rollOverlay = (rollTypeIcon !== '' && picUrl !== '')
        ? `<span class="position-absolute" style="top:-5px;left:0;z-index:3;pointer-events:none;">${iconMap(rollTypeIcon, 18, 18, false)}</span>`
        : '';
    const slotOverlay = (slotId !== '' && picUrl !== '')
        ? `<span class="position-absolute" style="top:-5px;right:0;z-index:3;pointer-events:none;">${iconMap(slotId, 18, 18, false)}</span>`
        : '';
    const fallbackFaceIcon = naturalWeapon ? 'natural_weapon' : attackIcon;
    const hitFace = picUrl !== ''
        ? `<img src="${escapeHtml(picUrl)}" alt="${escapeHtml(invName)}" class="img-thumbnail p-0" style="width:64px;height:64px;object-fit:cover;display:block;">`
        : `<span class="d-inline-flex align-items-center justify-content-center" style="width:64px;height:64px;border-radius:6px;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.2);">${iconMap(fallbackFaceIcon, 44, 44)}</span>`;
    // DRAGGABLE onto the action bar (owner only, real inventory weapons): the action-bar drag system
    // (action-bar.ts onAbilityPointerDown) recognises data-ft-weapon-inv-id and binds the weapon to a
    // slot as slot_type 'inventory'. A plain click still fires the attack (drag starts past a threshold).
    // touch-action:none lets the pointer-drag win over page scroll on touch. Natural weapons (no
    // inventory_id) and read-only views are not draggable.
    const draggable = invId !== '' && !ro;
    const hitDragAttr = draggable ? ` data-ft-weapon-inv-id="${escapeHtml(invId)}" style="touch-action:none;cursor:grab;"` : '';
    // The THROW tile is a SECOND draggable for a throwable weapon — bound with the throw variant so the
    // dagger can live on the bar as melee AND throw, each retaining its own action.
    const throwDragAttr = draggable ? ` data-ft-weapon-inv-id="${escapeHtml(invId)}" data-ft-weapon-variant="throw" style="touch-action:none;cursor:grab;"` : '';
    // When the tile CAN fire (live sheet, owner) a click attacks. When it's read-only (static monster
    // statblock / a non-owner) a `disabled` button would swallow ALL pointer events, so neither click
    // NOR press-and-hold could open the weapon's info — leaving the picture a dead end. Instead route the
    // read-only tile to open-info (view the weapon), matching the always-available view links on the name
    // and chips (owner 2026-07-10). No openId → keep it inert (nothing to open).
    const hitAction = ro
        ? (openId !== '' ? ` ${weaponAttrs('open-info', { itemId: openId })}` : ' disabled')
        : ` ${weaponAttrs('use-ability', { abilityId: openId, casterId })}`;
    const hitTitle = ro ? `View ${invName}` : invName;
    const hitBtn = `<button type="button" class="btn p-0 border-0 bg-transparent position-relative align-middle"${hitDragAttr}${hitAction} title="${escapeHtml(hitTitle)}">${hitFace}${rollOverlay}${slotOverlay}</button>`;
    // The throw tile mirrors the hit tile's construction — the weapon's OWN art as the face, the throw
    // type as the top-left corner overlay, and the same equipped-hand badge top-right — so the two read
    // as sibling action tiles (and the drag ghost / bound bar slot look like the thing being thrown).
    // Only the top-left badge differs: roll type on the hit tile, throw on this one.
    const throwOverlay = picUrl !== ''
        ? `<span class="position-absolute" style="top:-5px;left:0;z-index:3;pointer-events:none;">${iconMap('throw_weapon', 18, 18, false)}</span>`
        : '';
    const throwFace = picUrl !== ''
        ? `<img src="${escapeHtml(picUrl)}" alt="Throw ${escapeHtml(invName)}" class="img-thumbnail p-0" style="width:64px;height:64px;object-fit:cover;display:block;">`
        : `<span class="d-inline-flex align-items-center justify-content-center" style="width:64px;height:64px;border-radius:6px;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.2);">${iconMap('throw_weapon', 44, 44)}</span>`;
    const throwBtn = ftLayerHas(entry, 'item_throwable')
        ? `<button type="button" class="btn p-0 border-0 bg-transparent position-relative align-middle"${throwDragAttr}${ro ? ' disabled' : ` ${weaponAttrs('use-variable1', { abilityId: openId, variable1: 'throw_weapon', casterId })}`} title="Throw ${escapeHtml(invName)}">${throwFace}${throwOverlay}${slotOverlay}</button>`
        : '';

    // View-details (open-info) is a READ action — openItemInfo() renders the item into the side
    // panel cache-first, needs no edit rights, and re-validates any downstream edit server-side.
    // So the name / classification / enchant / ammo links are clickable REGARDLESS of canEdit
    // (canEdit still gates the hit/throw/damage BUTTONS). Gating these view links on canEdit greyed
    // out the natural-weapon names on the static item-creature statblock (isStatic → canEdit=false),
    // so a monster's natural weapons couldn't be clicked to inspect them (owner report 2026-07-10).
    // staticWeaponRow already renders these links unconditionally; this brings weaponRow in line.

    // weapon_classification — its own column is gone; render as an inline chip that sits with the
    // range/reach badges under the name. iconMap() falls back to the "?" help-circle, so guard empty.
    const classId = String(ftLayerValue(entry, 'weapon_classification') ?? '');
    const classChip = classId === ''
        ? ''
        : `<span role="button" class="d-inline-flex align-items-center" ${weaponInfoAttrs(classId)}>${iconMap(classId, 22, 22)}</span> `;

    // WEAPON INFO cell
    const nameHtml = openId !== ''
        ? `<span role="button" ${weaponInfoAttrs(openId)}>${escapeHtml(invName)}</span>`
        : escapeHtml(invName);
    let enchant = '';
    const enchantId = ftLayerValue(entry, 'enchantment_template_id');
    if (enchantId) {
        enchant = `<span role="button" ${weaponInfoAttrs(String(enchantId))}>${iconMap('item_enchanted')}</span>`;
    }
    let badges = '';
    // Reach is a DELTA on the wielder, not an absolute (owner 2026-08-15) — the engine adds
    // max(0, weapon_reach - 5) (is_target_in_range.php:127). A weapon at the default 5 extends
    // nothing and gets no badge.
    if (ftLayerHas(entry, 'weapon_reach')) {
        const reachExt = num(ftLayerValue(entry, 'weapon_reach')) - 5;
        if (Number.isFinite(reachExt) && reachExt > 0) {
            badges += `<span class="badge bg-info border">+${escapeHtml(String(reachExt))} ft. reach</span> `;
        }
    }
    if (ftLayerValue(entry, 'range_begin') !== null && ftLayerValue(entry, 'range_begin') !== undefined) {
        const rb = num(ftLayerValue(entry, 'range_begin')) * throwMult;
        const re = num(ftLayerValue(entry, 'range_end')) * throwMult;
        badges += `<span class="badge bg-light text-dark">${rb}${re !== rb ? ` / ${re}` : ''} ft</span> `;
    }
    if (ftLayerHas(entry, 'uses_ammo_classification')) {
        // ft_name_from_blob twin: nested slot name → blob-catalog identity → icon-registry tooltip
        // → raw UUID. The ammo classification (e.g. "Great Arrows") lives in additions.php's static
        // weapon_ammo_classification registry, NOT root.catalog, so catalogNameById misses it; its
        // icon tooltip is the only client-side name source (PHP's get_item_id_name DB leg).
        const ammoSlot = ftLayerSlotName(entry, 'uses_ammo_classification');
        const ammoId = String(ftLayerValue(entry, 'uses_ammo_classification') ?? '');
        // ammoSlot / ammoClassificationName come straight from the blob + registry (encoded); only the
        // catalogNameById leg is already decoded, so decode the picked value — a second pass on that
        // leg is a no-op (the helper short-circuits once there's no '&').
        const ammoLabel = decodeHtmlEntitiesForPlainText(ammoSlot || catalogNameById(ammoId) || ammoClassificationName(ammoId) || ammoId);
        // Only a real id gets the open-info affordance — ftLayerHas() counts '' as "present", so a
        // blank ammo value would otherwise render a role="button" badge whose click is a no-op
        // (the handler skips empty ids). Guard it like name/classChip/enchant above.
        badges += ammoId !== ''
            ? `<span class="badge bg-info" role="button" ${weaponInfoAttrs(ammoId)}>${escapeHtml(ammoLabel)}</span> `
            : `<span class="badge bg-info">${escapeHtml(ammoLabel)}</span> `;
    }

    // DAMAGE button — shows the dice + damage-type icon ("2d10 ☀"), like the ability effect buttons.
    // For a NATURAL WEAPON (an attack-ability node) reuse abilityEffectButton verbatim so the button
    // rolls EXACTLY like the Attack Abilities rows do: an example rollDice() when no target is selected
    // (so it's rollable straight off a static monster statblock, not a dead disabled button), and the
    // server damage_only path when a target IS selected. Falls back to the bespoke weapon face below
    // when the entry carries no `die` (nothing to example-roll — e.g. a typeless weapon) or for a real
    // equipped weapon on a live sheet, which keeps the existing damage_only button. (Owner 2026-07-10:
    // "make it just do a fake/example roll in the browser" — matches the ability rows.)
    // domScope 'natwpn' namespaces the multi-damage-type <select>'s DOM id so it can't collide with the
    // SAME ability's chooser rendered under Attack Abilities (Codex 2026-07-10). openId is the ability's
    // items.item_id for a natural weapon (no inventory_id), which is what the roll/use snippet needs.
    const naturalEffectBtn = naturalWeapon ? abilityEffectButton(caster, entry, openId, ro, 'natwpn') : '';
    const dmgTypeId = String(ftLayerValue(entry, 'damage_type') ?? '');
    const dmgDie = ftLayerValue(entry, 'die');
    let dmgFace = '';
    if (dmgDie !== null && dmgDie !== undefined && String(dmgDie) !== '') {
        // Apply damage_dice_quantity_mod to the shown dice count. Size enchantments (a Huge creature's
        // equipped Greataxe → 1d12 + 2 → 3d12) and other quantity mods contribute it, and the damage
        // roll adds it to the dice count (damage_type_roll.php). Without this the card shows the base
        // dice while the weapon actually rolls the up-sized dice. num('+2') → 2; absent → 0 (no change).
        const dmgDice = (num(ftLayerValue(entry, 'dice')) || 1) + num(ftLayerValue(entry, 'damage_dice_quantity_mod'));
        dmgFace = escapeHtml(`${dmgDice}d${dmgDie}`) + (dmgTypeId !== '' ? ' ' + iconMap(dmgTypeId, 18, 18) : '');
    } else if (dmgTypeId !== '') {
        dmgFace = iconMap(dmgTypeId);
    }
    const damageBtn = naturalEffectBtn
        || `<button type="button" class="btn btn-sm btn-danger text-nowrap fw-bold"${ro ? ' disabled' : ` ${weaponAttrs('use-variable1', { abilityId: openId, variable1: 'damage_only', casterId })}`}>${dmgFace}</button>`;

    // 3-COLUMN layout (owner redesign): PICTURE/attack (roll-type top-left + equipment-slot top-right
    // overlays) · WEAPON name with the range/reach + classification chips beneath · DAMAGE centered in
    // its own narrow column. The separate equipment-slot and weapon-classification columns are gone —
    // folded into the picture overlay and the name chips respectively.
    return `<tr>
        <td class="${naturalWeapon ? 'text-start' : 'text-center'} align-middle" style="width:72px;"><div class="d-inline-flex flex-column ${naturalWeapon ? 'align-items-start' : 'align-items-center'} gap-1">${hitBtn}${throwBtn}</div></td>
        <td class="align-middle ps-2"><div class="d-flex align-items-center flex-wrap gap-1">${classChip}<span>${nameHtml}${enchant}</span></div>${badges ? `<div class="d-flex flex-wrap align-items-center gap-1 mt-1">${badges}</div>` : ''}</td>
        <td class="text-center align-middle" style="width:96px;">${damageBtn}</td>
    </tr>`;
}

/** A lean, read-only equipped-weapon row for a STATIC item creature. Its inventory_equipped entries are
 *  leaned shells (name + weapon_classification, but no weapon_type/damage/die mechanics — those come via
 *  the creature's attack_ability, e.g. Cleave), so we just list it as equipped: classification + name +
 *  slot, no hit/damage buttons. ftLayerValue resolves the weapon_classification/equipment_slot shells to
 *  their UUID (same as weaponRow). */
function staticWeaponRow(entry: unknown, iconKey = 'weapons_equipped', leftAlign = false): string {
    const name = decodeHtmlEntitiesForPlainText(String(ftInventoryField(entry, 'inventory_name') ?? ftItemsCol(entry, 'item_name') ?? ''));
    const openId = String(ftItemsCol(entry, 'item_id') ?? '');
    const classId = String(ftLayerValue(entry, 'weapon_classification') ?? '');
    const slotId = String(ftLayerValue(entry, 'equipment_slot') ?? '');
    // 3-column parity with weaponRow: the slot rides as a top-right overlay on the icon tile, the
    // classification as an inline chip beneath the name.
    const slotOverlay = slotId !== ''
        ? `<span class="position-absolute" style="top:-5px;right:0;z-index:3;pointer-events:none;">${iconMap(slotId, 18, 18, false)}</span>`
        : '';
    const classChip = classId !== '' ? `<span role="button" class="d-inline-flex align-items-center" ${weaponInfoAttrs(classId)}>${iconMap(classId, 22, 22)}</span>` : '';
    const nameHtml = openId !== ''
        ? `<span role="button" ${weaponInfoAttrs(openId)}>${escapeHtml(name)}</span>`
        : escapeHtml(name);
    let enchant = '';
    const enchantId = ftLayerValue(entry, 'enchantment_template_id');
    if (enchantId) enchant = `<span role="button" ${weaponInfoAttrs(String(enchantId))}>${iconMap('item_enchanted')}</span>`;
    return `<tr>
        <td class="${leftAlign ? 'text-start' : 'text-center'} align-middle" style="width:72px;"><span class="position-relative d-inline-block">${iconMap(iconKey, 44, 44)}${slotOverlay}</span></td>
        <td class="align-middle ps-2"><div class="d-flex align-items-center flex-wrap gap-1">${classChip}<span>${nameHtml}${enchant}</span></div></td>
        <td class="text-center align-middle" style="width:96px;"></td>
    </tr>`;
}

/** Icon-registry tooltip → display name for an ammo classification UUID. These live in
 *  additions.php's static weapon_ammo_classification registry (not the blob catalog), but each
 *  carries an icon whose tooltip is its name (e.g. "Great Arrows"). Mirrors iconRegistryName(). */
function ammoClassificationName(id: string): string {
    const reg = window.FT_ICON_REGISTRY;
    if (!reg || !id) return '';
    const e = reg[id] || reg[id.toUpperCase()] || reg[id.toLowerCase()];
    const t = e && typeof e.tooltip === 'string' ? e.tooltip : '';
    return (t !== '' && t !== 'Unknown') ? t : '';
}

/** Read a layer slot's nested item_name (for ammo label) when present. */
function ftLayerSlotName(entry: unknown, layer: string): string {
    const entryRecord = recordOf(entry);
    const bucket = recordOf(entryRecord?.item_layer_types)?.[layer] ?? entryRecord?.[layer];
    const slot = Array.isArray(bucket) ? bucket[0] : bucket;
    if (slot && typeof slot === 'object') {
        const slotRecord = recordOf(slot);
        const nm = recordOf(slotRecord?.items)?.item_name ?? slotRecord?.item_name;
        if (typeof nm === 'string') return nm;
    }
    return '';
}

/** Full item_arr (not a deduped shell): carries its own non-empty item_layer_types bucket. */
function isFullNode(node: unknown): boolean {
    const ilt = recordOf(node)?.item_layer_types;
    return !!ilt && typeof ilt === 'object' && !Array.isArray(ilt) && Object.keys(ilt).length > 0;
}

/** Normalize a computed-cluster slot to an entries array (absent/''/scalar/array). */
function clusterEntries(v: unknown): unknown[] {
    return Array.isArray(v) ? v : (v !== null && v !== undefined && v !== '' ? [v] : []);
}

/** Cold-cache hydration for natural-weapon refs whose FULL def isn't in any client blob (the
 *  default unarmed Slam is identity-only in the encounter root catalog — the PHP table hydrated
 *  it server-side via query_full_item_arr). Fetch each missing def ONCE into the objectCache,
 *  then surgically repaint this caster's panes so the lean name rows upgrade to full attack/
 *  damage rows. Ids that fetch null (deleted/no-permission) stay lean and are never re-tried. */
const natWeaponFetchTried = new Set<string>();
function queueNaturalWeaponHydration(caster: CachedCaster, ids: string[]): void {
    const fetchObj = window.fetchAndCacheObject;
    if (typeof fetchObj !== 'function') return;
    const todo = ids.filter((id) => id !== '' && !natWeaponFetchTried.has(id.toUpperCase()));
    if (todo.length === 0) return;
    todo.forEach((id) => natWeaponFetchTried.add(id.toUpperCase()));
    const ecRaw = caster.encounter_creatures?.encounter_creature_id;
    const casterId = String((Array.isArray(ecRaw) ? ecRaw[0] : ecRaw) ?? ftItemsCol(caster, 'item_id') ?? '');
    void Promise.all(todo.map((id) => fetchObj(id))).then((results: unknown[]) => {
        if (!results.some(Boolean) || casterId === '') return;
        window.ftRerenderPcSheet?.(casterId);
    });
}

/**
 * Port of the "NATURAL WEAPONS" block in _weapons_equipped_inc.php (legacy PHP table retired
 * 2026-07-01 — this renderer now covers ALL its cases): the auto-equipped natural_weapon entries
 * rendered as the same 3-column weapon table as Equipped Weapons. The PHP re-queried each entry
 * via query_full_item_arr; client-side, full-nested entries render directly, blob-resolvable refs
 * via the cache walker, and identity-only refs render a lean name row then upgrade in place once
 * queueNaturalWeaponHydration pulls their def. Returns '' for creatures with no natural weapons.
 */
export function renderNaturalWeapons(caster: CachedCaster, casterId: string, isStatic = false): string {
    // PHP layer_entries() bridges the computed.creature.* clusters; the JS walk does NOT
    // ([[feedback_ftlayervalue_no_computed_bridge]]). Mirror its precedence: the CURATED
    // computed.creature.abilities.natural_weapon list (PC casters post-finalize — where the
    // default unarmed Slam lives, and with overridden abilities already suppressed) REPLACES the
    // raw layer walk when present; computed.creature.natural_weapon (the Lich's skill-granted
    // tier-9 Slam) merges in either way (same bridge the list-display card's block does).
    const abilities = clusterEntries(caster.computed?.creature?.abilities?.natural_weapon);
    const layerEntries = (abilities.length === 0 && ftLayerHas(caster, 'natural_weapon'))
        ? ftLayerEntries(caster, 'natural_weapon') : [];
    const entries = [
        ...abilities,
        ...(Array.isArray(layerEntries) ? layerEntries : []),
        ...clusterEntries(caster.computed?.creature?.natural_weapon),
    ];
    if (entries.length === 0) return '';

    const canEdit = !isStatic && typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    let rows = '';
    const seen = new Set<string>();
    const unresolved: string[] = [];
    for (const entry of entries) {
        const uuid = uuidOf(entry);
        if (uuid !== '') {
            if (seen.has(uuid.toLowerCase())) continue;
            seen.add(uuid.toLowerCase());
        }
        let node: unknown = recordOf(entry);
        if (!node || !isFullNode(node)) {
            // Deduped shell / v25 bare-UUID ref — re-resolve the full in-blob copy by id
            // (object-card bundle walker, which also reads root.catalog full defs).
            const full = uuid !== '' && typeof window.ftFindFullNestedItem === 'function'
                ? window.ftFindFullNestedItem(uuid)
                : null;
            if (full) node = full;
        }
        if (!node) {
            // Bare ref whose full def isn't in-blob (identity-only catalog row): lean name row —
            // upgraded in place once the hydration fetch below lands its full def.
            if (uuid === '') continue;
            node = { items: { item_id: uuid, item_name: catalogNameById(uuid) || uuid } };
        }
        if (isFullNode(node)) {
            rows += weaponRow(caster, node, canEdit, casterId, true);
        } else {
            rows += staticWeaponRow(node, 'natural_weapon', true);
            if (uuid !== '') unresolved.push(uuid);
        }
    }
    queueNaturalWeaponHydration(caster, unresolved);
    if (rows === '') return '';

    // Column-icon thead dropped (owner request) — the big section header + per-row icons carry it.
    const header = `<h5 class="ft-weapons-tab-h ft-ability-hdr text-start ps-3 mt-1 mb-0 py-2 rounded-top" style="--ft-ah:#fbbf24;">${iconMap('natural_weapon', 22, 22)}<span class="ms-2">Natural Weapons</span></h5>`;
    return `<div class="ft-pc-natural-weapons">${header}<div class="table-responsive-xxl mb-3"><table class="table table-hover table-sm"><tbody>${rows}</tbody></table></div></div>`;
}

export function renderEquippedWeapons(caster: CachedCaster, casterId: string, isStatic = false): string {
    // isStatic (item-creature statblock): force read-only — the hit/throw/damage buttons don't fire.
    const canEdit = !isStatic && typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    // Column-icon thead dropped (owner request) — see renderNaturalWeapons.
    const header = `<h5 class="ft-weapons-tab-h ft-ability-hdr text-start ps-3 mt-1 mb-0 py-2 rounded-top" style="--ft-ah:#5fd28a;">${iconMap('weapons_equipped', 22, 22)}<span class="ms-2">Equipped Weapons</span></h5>`;

    // Scan inventory directly for an equipped weapon per slot (robust — avoids the
    // count_*_weapon_equipped top-level scalars whose serialized location is uncertain).
    // Shapeshift-absorbed rows (inventory_private === 2, wildshift) keep their `equipped` slot
    // only for the revert — filter them out so an absorbed weapon never renders as an attack tile.
    const inv = ftLayerEntries(caster, 'inventory').filter(
        (e) => num(ftInventoryField(e, 'inventory_private')) !== 2,
    );
    const findSlot = (slot: string): unknown => inv.find(
        (e) => equippedSlotOf(e) === slot && String(ftItemsCol(e, 'item_type') ?? '') === 'weapon',
    );
    const mainE = findSlot(SLOT_MAIN);
    const offE = findSlot(SLOT_OFF);
    const bothE = findSlot(SLOT_BOTH);

    if (!mainE && !offE && !bothE) {
        // Item-creature model: equipped gear lives in inventory_equipped (full item_arrs, no per-item
        // `equipped` hand-UUID + no count_* layers), so the slot scan above finds nothing. Render any
        // weapons sitting in that bucket instead (the orc minion's Greataxe). Gated on isStatic so a
        // live PC/encounter creature (which uses the `inventory` bucket) is never affected.
        const eqWeapons = (isStatic ? ftLayerEntries(caster, 'inventory_equipped') : []).filter(
            (e) => String(ftItemsCol(e, 'item_type') ?? '') === 'weapon',
        );
        if (eqWeapons.length > 0) {
            const eqRows = eqWeapons.map((e) => staticWeaponRow(e)).join('');
            return `<div class="ft-pc-weapons">${header}<div class="table-responsive-xxl mb-3"><table class="table table-hover table-sm"><tbody>${eqRows}</tbody></table></div></div>`;
        }
        // No weapons in any hand slot → suppress the whole section. The standalone "No weapons
        // equipped." banner is just noise (esp. for creatures that fight with natural weapons),
        // so render nothing rather than an empty-state header.
        return '';
    }

    let rows = '';
    if (mainE) rows += weaponRow(caster, mainE, canEdit, casterId);
    if (offE) rows += weaponRow(caster, offE, canEdit, casterId);
    if (bothE) rows += weaponRow(caster, bothE, canEdit, casterId);

    return `<div class="ft-pc-weapons">${header}<div class="table-responsive-xxl mb-3"><table class="table table-hover table-sm"><tbody>${rows}</tbody></table></div></div>`;
}
